import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useAuth0 } from 'Auth';
import { useMutation, useQuery } from '@apollo/react-hooks';

import {
  AnimatedScreen,
  HRule,
  LoadingPage,
  useToast,
} from 'Shared/components';
import { useAsyncError } from 'Shared/hooks';

import { getRefetchPartnerQuery } from 'Admin/components/Router';
import { RateCallOneReports } from 'Admin/Partners';
import { PartnerContext } from 'Admin/utils';
import { updateProtectedAttributes } from 'Admin/authPolicies';
import {
  yupEinValidator,
  yupNpnValidator,
  yupPhoneValidator,
  yupZipValidator,
} from 'Admin/utils/formValidators';
import { PartnerForm, PartnerFormValues } from 'Admin/Partners/components';
import { NpnStatusBanner } from 'Admin/components/NpnStatusBanner';
import { NPN_STATUS_MAPPINGS } from 'Admin/components/NpnStatus/NpnStatus';
import { useLazyBaseApi } from 'Shared/services/Api';
import { GetPartnerConfigType } from 'Admin/services/endpoints/GetPartnerConfig';
import { transformPostPartnerConfig } from 'Admin/services/endpoints/PostPartnerConfig';
import {
  CREATE_GRANTS,
  DELETE_GRANTS,
  UPDATE_PARTNER,
} from 'Admin/Partners/graphql';
import { combineStrings, isMissing } from 'Shared/utils';
import { SystemRolesType } from 'Admin/Users/components';
import { GET_ROLES_BY_PARTNER_QUERY } from 'Admin/Users/graphql';
import { MovePartner } from './MovePartner';

const partnerDetailsSchema = Yup.object().shape({
  name: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Required', value => {
      return value ? value.trim().length > 0 : false;
    }),
  channel: Yup.string(),
  commissionStructure: Yup.string(),
  licensedAgency: Yup.object().shape({
    phoneNumber: yupPhoneValidator(),
    employerIdentificationNumber: Yup.string().when('soleProprietor', {
      is: false,
      then: yupEinValidator(),
    }),
    nationalProducerNumber: Yup.string().when('soleProprietor', {
      is: false,
      then: yupNpnValidator().required('Required'),
    }),
    soleProprietor: Yup.boolean(),
  }),
  address: Yup.object().shape({
    lineOne: Yup.string(),
    lineTwo: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zipCode: yupZipValidator(),
  }),
  quotePdfSettings: Yup.object({
    inheritParentInformation: Yup.boolean(),
    customizeContactInformation: Yup.boolean(),
    contactInformation: Yup.object({
      agencyName: Yup.string(),
      agentName: Yup.string(),
      emailAddress: Yup.string().email('Must be a valid email address'),
      phoneNumber: yupPhoneValidator(),
    }),
  }),
  productOffering: Yup.object({
    nonStandardAuto: Yup.boolean(),
    standardAuto: Yup.boolean(),
  }),
  sourceOfSale: Yup.string(),
});

type PartnerProtectedUpdateAttributes = {
  channel?: string;
  commissionStructure?: string;
  rateCallOneReports?: RateCallOneReports;
  productOffering?: {
    nonStandardAuto?: boolean;
    standardAuto?: boolean;
  };
  sourceOfSale?: string;
};

type UpdatePartnerPayload = {
  updatePartner: {
    partner: {
      name: string;
    } & PartnerProtectedUpdateAttributes;
  };
  licensedAgencyCreateOrUpdate: {
    licensedAgency: {
      phoneNumber: string;
      soleProprietor: boolean;
      employerIdentificationNumber: string;
      nationalProducerNumber: string;
    };
  };
  createOrUpdatePartnerAddress: {
    address: {
      lineOne: string;
      lineTwo: string;
      city: string;
      zipCode: string;
      state: string;
    };
  };
};

const START_QUOTES_IN_PORTAL_ROLE_NAME = 'agency_start_quotes_in_portal';

export const PartnerDetails: FC = () => {
  const { user } = useAuth0();
  const { toast } = useToast();
  const { loading: loadingCurrentPartner, currentPartner } = useContext(
    PartnerContext
  );
  const throwError = useAsyncError();

  const refetchCurrentPartnerQuery = getRefetchPartnerQuery(
    currentPartner?.id,
    user!.effectiveGrants
  );

  const { data: roles } = useQuery<SystemRolesType>(
    GET_ROLES_BY_PARTNER_QUERY,
    {
      variables: {
        partnerId: currentPartner?.id,
      },
      skip: isMissing(currentPartner?.id),
      fetchPolicy: 'network-only',
    }
  );

  const isPrincipalRoleAssignable = roles?.systemRoles.find(
    r => r.name === 'principal'
  )?.assignable;

  const [updatePartner, { error: updatePartnerError }] = useMutation<
    UpdatePartnerPayload,
    PartnerProtectedUpdateAttributes & {
      id: string;
      name: string;
      phoneNumber: string;
      nationalProducerNumber: string;
      employerIdentificationNumber: string;
      soleProprietor: boolean;
      lineOne: string;
      lineTwo: string;
      city: string;
      zip: string;
      state: string;
    }
  >(UPDATE_PARTNER, {
    errorPolicy: 'ignore',
    awaitRefetchQueries: true,
    refetchQueries: [refetchCurrentPartnerQuery],
  });

  const [createPartnerGrant] = useMutation<
    {},
    {
      grants: Array<{
        partnerId: string;
        partnerRoleId: string;
      }>;
    }
  >(CREATE_GRANTS, {
    awaitRefetchQueries: true,
    refetchQueries: [refetchCurrentPartnerQuery],
  });

  const [deletePartnerGrant] = useMutation<
    {},
    {
      grants: Array<{
        id: string;
      }>;
    }
  >(DELETE_GRANTS, {
    awaitRefetchQueries: true,
    refetchQueries: [refetchCurrentPartnerQuery],
  });

  const canUpdateProtectedAttributes = updateProtectedAttributes(
    user!.effectiveGrants
  );

  const [partnerConfig, setPartnerConfig] = useState<GetPartnerConfigType>();
  const [partnerConfigLoading, setPartnerConfigLoading] = useState<boolean>(
    false
  );

  const [
    { loading: apiLoadingPartnerConfig },
    getPartnerConfig,
  ] = useLazyBaseApi<GetPartnerConfigType>({});

  const [{ error: postPartnerConfigError }, postPartnerConfig] = useLazyBaseApi<
    GetPartnerConfigType
  >({
    method: 'POST',
    url: `/agent_services/partner/${currentPartner?.id}/configuration`,
    transformRequest: transformPostPartnerConfig,
  });

  useEffect(() => {
    // wait for current partner to load before fetching the config
    if (
      canUpdateProtectedAttributes &&
      !partnerConfig &&
      !partnerConfigLoading &&
      !loadingCurrentPartner &&
      currentPartner?.id &&
      !apiLoadingPartnerConfig
    ) {
      (async () => {
        try {
          setPartnerConfigLoading(true);

          const getPartnerConfigResponse = await getPartnerConfig({
            url: `/agent_services/partner/${currentPartner?.id}/configuration`,
          });

          setPartnerConfig(getPartnerConfigResponse?.data);
        } catch (e) {
          throwError(e);
        } finally {
          setPartnerConfigLoading(false);
        }
      })();
    }
  }, [
    canUpdateProtectedAttributes,
    loadingCurrentPartner,
    currentPartner,
    partnerConfig,
    getPartnerConfig,
    partnerConfigLoading,
    throwError,
    apiLoadingPartnerConfig,
  ]);

  const initialQuotePdfSettings = useMemo(() => {
    const isInheritingFromParent = Boolean(
      partnerConfig?.quote_pdf?.inherit_parent_information
    );

    if (isInheritingFromParent) {
      return {
        inheritParentInformation: true,
        customizeContactInformation: false,
        contactInformation: {
          agencyName: '',
          agentName: '',
          emailAddress: '',
          phoneNumber: '',
        },
        readOnlySettings: partnerConfig,
      };
    } else {
      return {
        inheritParentInformation: false,
        customizeContactInformation:
          partnerConfig?.quote_pdf?.customize_contact_information,
        contactInformation: {
          agencyName:
            partnerConfig?.quote_pdf?.custom_contact_information?.agency_name ||
            '',
          agentName:
            partnerConfig?.quote_pdf?.custom_contact_information?.agent_name ||
            '',
          emailAddress:
            partnerConfig?.quote_pdf?.custom_contact_information
              ?.email_address || '',
          phoneNumber:
            partnerConfig?.quote_pdf?.custom_contact_information
              ?.phone_number || '',
        },
      };
    }
  }, [partnerConfig]);

  if (loadingCurrentPartner || partnerConfigLoading) {
    return <LoadingPage />;
  }

  if (!currentPartner) {
    return null;
  }

  const { lineOne, lineTwo, city, state, zipCode } =
    currentPartner.address || {};
  const {
    phoneNumber,
    employerIdentificationNumber,
    nationalProducerNumber,
    soleProprietor,
  } = currentPartner.licensedAgency || {};

  // Partner CAN BE assigned "Start Quotes In Portal" role
  const startQuotesInPortalRoleId = currentPartner.assignableRoles.find(
    role => {
      return role.name === START_QUOTES_IN_PORTAL_ROLE_NAME;
    }
  )?.id;

  // Partner IS assigned "Start Quotes In Portal" role
  const partnerCanQuotePortalGrant = currentPartner.partnerGrants.find(
    grant => {
      return grant.role.name === START_QUOTES_IN_PORTAL_ROLE_NAME;
    }
  );

  // Boolean value for "Start Quotes In Portal"
  const canQuotePortal = !!partnerCanQuotePortalGrant;

  return (
    <AnimatedScreen testId="partner-details">
      <Formik<PartnerFormValues>
        // reinitialize because quotePdfSettings initial values come from api after current partner exists
        enableReinitialize
        initialValues={{
          name: currentPartner.name || '',
          principalAgent: currentPartner.principalUser
            ? combineStrings([
                currentPartner.principalUser.firstName,
                currentPartner.principalUser.lastName,
              ])
            : '',
          channel: currentPartner.channel || '',
          commissionStructure: currentPartner.commissionStructure || '',
          sourceOfSale: currentPartner.sourceOfSale || '',
          rateCallOneReports: currentPartner.rateCallOneReports || 'inherit',
          licensedAgency: {
            phoneNumber: phoneNumber || '',
            employerIdentificationNumber: employerIdentificationNumber || '',
            nationalProducerNumber: nationalProducerNumber || '',
            soleProprietor: soleProprietor || false,
          },
          address: {
            lineOne: lineOne || '',
            lineTwo: lineTwo || '',
            city: city || '',
            zipCode: zipCode || '',
            state: state || '',
          },
          canQuotePortal: canQuotePortal,
          npnStatus: currentPartner.npnStatus || 'not_applicable',
          quotePdfSettings: initialQuotePdfSettings,
          productOffering: {
            nonStandardAuto: currentPartner.partnerAvailableProducts?.some(
              product =>
                product.productType === 'personal_auto' &&
                product.productSegment === 'nonStandardAuto'
            ),
            standardAuto: currentPartner.partnerAvailableProducts?.some(
              product =>
                product.productType === 'personal_auto' &&
                product.productSegment === 'standardAuto'
            ),
          },
        }}
        initialStatus={{
          nationalProducerNumber:
            currentPartner.npnStatus === 'invalid'
              ? NPN_STATUS_MAPPINGS[currentPartner.npnStatus].message
              : undefined,
        }}
        validationSchema={partnerDetailsSchema}
        validateOnBlur={false}
        onSubmit={(values, { setSubmitting }) => {
          const protectedAttributes: PartnerProtectedUpdateAttributes = {};
          if (canUpdateProtectedAttributes) {
            // do not send these if you cannot edit the field
            protectedAttributes.channel =
              values.channel === '' ? undefined : values.channel;
            protectedAttributes.commissionStructure =
              values.commissionStructure === ''
                ? undefined
                : values.commissionStructure;
            protectedAttributes.rateCallOneReports = values.rateCallOneReports;
            protectedAttributes.productOffering = values.productOffering;
            protectedAttributes.sourceOfSale =
              values.sourceOfSale === '' ? undefined : values.sourceOfSale;
          }

          // update partner details
          updatePartner({
            variables: {
              id: currentPartner.id,
              name: values.name,
              phoneNumber: values.licensedAgency.phoneNumber,
              nationalProducerNumber:
                values.licensedAgency.nationalProducerNumber,
              employerIdentificationNumber:
                values.licensedAgency.employerIdentificationNumber,
              soleProprietor: values.licensedAgency.soleProprietor,
              lineOne: values.address.lineOne,
              lineTwo: values.address.lineTwo,
              city: values.address.city,
              zip: values.address.zipCode,
              state: values.address.state,
              ...protectedAttributes,
            },
          })
            // then save partner roles
            .then(() => {
              // skip if value is not changed
              if (values.canQuotePortal === canQuotePortal) return;

              // if value is true and "Start Quotes In Portal" role is assignable
              if (values.canQuotePortal && startQuotesInPortalRoleId) {
                createPartnerGrant({
                  variables: {
                    grants: [
                      {
                        partnerId: currentPartner.id,
                        partnerRoleId: startQuotesInPortalRoleId,
                      },
                    ],
                  },
                });
                // else if "Start Quotes In Portal" role is already assigned
              } else if (partnerCanQuotePortalGrant) {
                deletePartnerGrant({
                  variables: {
                    grants: [
                      {
                        id: partnerCanQuotePortalGrant.id,
                      },
                    ],
                  },
                });
              }
            })
            // then save the partner config settings
            .then(async () => {
              if (canUpdateProtectedAttributes) {
                try {
                  const postPartnerConfigResponse = await postPartnerConfig({
                    data: values.quotePdfSettings,
                  });
                  setPartnerConfig(postPartnerConfigResponse.data);
                } catch (e) {
                  throw e;
                }
              }
            })
            .then(() => {
              toast({
                message: 'Page has been saved.',
                type: 'success',
                position: 'bottom',
                withSecondaryNav: true,
              });
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({ values }) => {
          return (
            <>
              {!values.licensedAgency.soleProprietor && (
                <NpnStatusBanner npnStatus={currentPartner.npnStatus} />
              )}
              <h1>Details</h1>
              <HRule size="lg" />
              <PartnerForm
                type="edit"
                footerError={
                  updatePartnerError?.message || postPartnerConfigError?.message
                }
                startQuotesInPortalRoleIsAssignable={
                  !!startQuotesInPortalRoleId
                }
                isPrincipalRoleAssignable={isPrincipalRoleAssignable}
                isClearcoverUser={user?.partner?.name === 'clearcover'}
                currentPartner={currentPartner}
                canEditRateCall={canUpdateProtectedAttributes}
                canEditPdfSettings={canUpdateProtectedAttributes}
              />
              <MovePartner currentPartner={currentPartner} />
            </>
          );
        }}
      </Formik>
    </AnimatedScreen>
  );
};
